// src/components/LoadingSpinner.tsx
import React from "react";

type LoadingSpinnerProps = {
  /** Texto accesible/visible bajo el spinner */
  label?: string;
  /** Ocupa toda la pantalla y centra el loader */
  fullscreen?: boolean;
  /** Tamaño del spinner en píxeles */
  size?: number;
};

export default function LoadingSpinner({
  label = "Cargando…",
  fullscreen = false,
  size = 40,
}: LoadingSpinnerProps): React.ReactElement {
  const ringStyle: React.CSSProperties = {
    width: size,
    height: size,
    borderWidth: Math.max(2, Math.floor(size * 0.08)),
    borderStyle: "solid",
    borderColor: "color-mix(in oklab, var(--color-primary) 22%, transparent)",
    borderTopColor: "var(--color-primary)",
  };

  return (
    <div
      className={[
        fullscreen ? "min-h-[60vh] md:min-h-screen" : "",
        "grid place-items-center p-6",
      ].join(" ")}
      role="status"
      aria-live="polite"
    >
      {/* estilos de animación locales (no requieren tocar tailwind.config) */}
      <style>{`
        @keyframes r4-slide {
          0%   { transform: translateX(-120%); }
          100% { transform: translateX(320%); }
        }
        @keyframes r4-float {
          0%,100% { transform: translateY(0); }
          50%     { transform: translateY(-2px); }
        }
      `}</style>

      <div className="w-full max-w-sm">
        {/* Tarjeta “glass/tonal” */}
        <div className="relative overflow-hidden rounded-2xl border border-surface bg-surface/80 p-5 shadow-soft backdrop-blur">
          {/* halo decorativo suave */}
          <div
            aria-hidden
            className="pointer-events-none absolute -left-10 -top-10 h-32 w-32 rounded-full opacity-40 blur-2xl"
            style={{
              background:
                "radial-gradient(closest-side, color-mix(in oklab, var(--color-primary) 22%, transparent), transparent 70%)",
            }}
          />

          {/* Spinner */}
          <div className="relative mx-auto grid place-items-center">
            <div
              className="animate-spin rounded-full"
              style={ringStyle}
              aria-hidden
            />
            {/* sparkle */}
            <span
              aria-hidden
              className="absolute"
              style={{
                width: Math.max(6, Math.floor(size * 0.18)),
                height: Math.max(6, Math.floor(size * 0.18)),
                borderRadius: 999,
                background: "var(--color-primary)",
                boxShadow:
                  "0 0 0 6px color-mix(in oklab, var(--color-primary) 12%, transparent)",
                animation: "r4-float 1.6s ease-in-out infinite",
                top: `-${Math.max(2, Math.floor(size * 0.08))}px`,
                right: `-${Math.max(2, Math.floor(size * 0.08))}px`,
              }}
            />
          </div>

          {/* Label */}
          <div className="mt-4 text-center">
            <div
              className="mx-auto w-max bg-clip-text text-base font-medium text-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(90deg, var(--color-primary), color-mix(in oklab, var(--color-primary) 60%, var(--color-onSurface)))",
              }}
            >
              {label}
            </div>
          </div>

          {/* Barra indeterminada */}
          <div
            className="mt-4 h-1.5 w-full overflow-hidden rounded-full"
            style={{
              background:
                "color-mix(in oklab, var(--color-onSurface) 10%, transparent)",
            }}
            aria-hidden
          >
            <div
              className="h-full w-1/3 rounded-full"
              style={{
                background:
                  "linear-gradient(90deg, color-mix(in oklab, var(--color-primary) 55%, transparent), var(--color-primary))",
                animation: "r4-slide 1.1s ease-in-out infinite",
              }}
            />
          </div>
        </div>

        {/* Nota de compatibilidad (opcional) */}
        <span className="sr-only">{label}</span>
      </div>
    </div>
  );
}
